<template>
	<transition name="fade">
		<div
			v-if="showFlow"
			class="onboarding-overlay"
			:class="{ 'is-loading': isOverlayLoading }">
			<span class="ambient ambient-1"></span>
			<span class="ambient ambient-2"></span>
			<span class="ambient ambient-3"></span>
			<div class="onboarding-shell">
				<div class="onboarding-header">
					<div class="onboarding-badge">
						<i class="bi bi-compass-fill"></i>
					</div>
					<p class="onboarding-sub">Welcome to Grub Vision</p>
				</div>
				<div class="onboarding-progress">
					<div
						class="onboarding-progress-bar"
						:style="{ width: progress + '%' }"></div>
				</div>
				<div class="onboarding-steps">
					<div
						:ref="(el) => setStepRef(el, index)"
						v-for="(step, index) in steps"
						:key="step.label"
						class="onboarding-step"
						:class="{
							'is-complete': index < currentStep,
							'is-active': index === currentStep,
						}">
						<div class="step-icon">
							<i :class="['bi', step.icon]"></i>
						</div>
						<span class="step-label">{{ step.label }}</span>
					</div>
				</div>

				<div class="onboarding-body">
					<transition name="slide-fade" mode="out-in">
						<div
							v-if="isOverlayLoading"
							key="step-loading"
							class="onboarding-loading">
							<div class="spinner-border text-primary" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
							<p class="loading-text">
								{{
									savingMessage ||
									(isSaving
										? "Tuning your journey…"
										: "Loading your onboarding…")
								}}
							</p>
						</div>

						<template v-else>
							<OnboardingStepWelcome
								v-if="currentStep === 0"
								key="step-welcome" />

							<OnboardingStepBirthday
								v-else-if="currentStep === 1"
								key="step-birthday"
								v-model="form.birthday"
								v-model:gender="form.birthGender" />

							<OnboardingStepAllergies
								v-else-if="currentStep === 2"
								key="step-allergies"
								v-model="form.allergies"
								v-model:noneSelected="form.noAllergies" />

							<OnboardingStepRestrictions
								v-else-if="currentStep === 3"
								key="step-restrictions"
								v-model="form.restrictions"
								v-model:noneSelected="form.noRestrictions" />

							<OnboardingStepGoals
								v-else-if="currentStep === 4"
								key="step-goals"
								v-model="form.maxCalories" />

							<OnboardingStepReferral
								v-else-if="currentStep === 5"
								key="step-referral"
								v-model="form.referralSource"
								v-model:other="form.referralSourceOther" />

							<OnboardingStepComplete v-else key="step-finish" />
						</template>
					</transition>
				</div>

				<div class="onboarding-actions">
					<button
						class="btn btn-outline-secondary"
						type="button"
						@click="goBack"
						:disabled="isFirstStep || isSaving">
						Back
					</button>
					<button
						class="btn btn-success onboarding-primary"
						type="button"
						@click="goNext"
						:disabled="!canProceed">
						{{ isLastStep ? "Finish" : "Next" }}
					</button>
				</div>
			</div>
		</div>
	</transition>
</template>

<script setup>
	import {
		computed,
		onBeforeUnmount,
		onMounted,
		nextTick,
		reactive,
		ref,
		watch,
	} from "vue"
	import { useUserStore } from "@/stores/user"
	import { fetchWithAuth } from "@/utils/api-client"
	import Swal from "sweetalert2"
	import { Preferences } from "@capacitor/preferences"
	import OnboardingStepWelcome from "./onboarding/OnboardingStepWelcome.vue"
	import OnboardingStepBirthday from "./onboarding/OnboardingStepBirthday.vue"
	import OnboardingStepAllergies from "./onboarding/OnboardingStepAllergies.vue"
	import OnboardingStepRestrictions from "./onboarding/OnboardingStepRestrictions.vue"
	import OnboardingStepGoals from "./onboarding/OnboardingStepGoals.vue"
	import OnboardingStepReferral from "./onboarding/OnboardingStepReferral.vue"
	import OnboardingStepComplete from "./onboarding/OnboardingStepComplete.vue"
	import {
		normalizeReferralSource,
		normalizeReferralSourceOther,
		validateReferralDetails,
	} from "../../../shared/referralSources.js"

	const steps = [
		{ label: "Welcome", icon: "bi-stars" },
		{ label: "Milestones", icon: "bi-calendar2-heart" },
		{ label: "Allergies", icon: "bi-heart-pulse" },
		{ label: "Restrictions", icon: "bi-card-checklist" },
		{ label: "Goals", icon: "bi-activity" },
		{ label: "Source", icon: "bi-megaphone" },
		{ label: "Launch", icon: "bi-rocket-takeoff" },
	]

	const totalSteps = steps.length
	const userStore = useUserStore()
	const showFlow = ref(false)
	const isLoading = ref(true)
	const isSaving = ref(false)
	const savingMessage = ref("")
	const currentStep = ref(0)
	const stepRefs = []
	const originalOverflow = { body: "", html: "" }
	const hasBootstrapped = ref(false)
	const ONBOARDING_PREF_KEY = "gv-onboarding-complete"

	const form = reactive({
		birthday: "",
		birthGender: "",
		allergies: "",
		restrictions: "",
		noAllergies: false,
		noRestrictions: false,
		maxCalories: "",
		referralSource: "",
		referralSourceOther: "",
	})

	const isFirstStep = computed(() => currentStep.value === 0)
	const isLastStep = computed(() => currentStep.value === totalSteps - 1)
	const progress = computed(() => ((currentStep.value + 1) / totalSteps) * 100)
	const canProceed = computed(() => {
		if (isSaving.value) return false
		return !validateStep(currentStep.value)
	})

	const isOverlayLoading = computed(() => isLoading.value || isSaving.value)

	const sanitize = (value) =>
		typeof value === "string" ? value.trim() : value ?? ""

	const loadPreferenceComplete = async () => {
		try {
			const { value } = await Preferences.get({ key: ONBOARDING_PREF_KEY })
			return value === "true"
		} catch {
			return false
		}
	}

	const markPreferenceComplete = async () => {
		try {
			await Preferences.set({ key: ONBOARDING_PREF_KEY, value: "true" })
		} catch {
			// ignore preference errors
		}
	}

	const validateStep = (step) => {
		switch (step) {
			case 1: {
				const value = sanitize(form.birthday)
				if (!value) return "Please add your birthday before continuing."
				if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
					return "Birthday must be in YYYY-MM-DD format."
				}
				const dateValue = new Date(`${value}T00:00:00Z`)
				if (Number.isNaN(dateValue.getTime())) {
					return "Birthday is not a valid date."
				}
				if (dateValue > new Date()) {
					return "Birthday cannot be in the future."
				}
				const gender = sanitize(form.birthGender).toLowerCase()
				if (!gender) return "Please select a birth gender."
				if (gender !== "male" && gender !== "female") {
					return "Birth gender must be male or female."
				}
				return null
			}
			case 2: {
				if (form.noAllergies) return null
				const value = sanitize(form.allergies)
				if (!value || value.length < 3) {
					return "Tell us about your allergies before moving on."
				}
				return null
			}
			case 3: {
				if (form.noRestrictions) return null
				const value = sanitize(form.restrictions)
				if (!value || value.length < 3) {
					return "Share any dietary restrictions before continuing."
				}
				return null
			}
			case 4: {
				const value = sanitize(form.maxCalories)
				if (!value) return null
				const parsed = Number(value)
				if (Number.isNaN(parsed) || parsed <= 0) {
					return "Max calories must be a positive number or left blank."
				}
				return null
			}
			case 5:
				return validateReferralDetails({
					referralSource: form.referralSource,
					referralSourceOther: form.referralSourceOther,
				})
			default:
				return null
		}
	}

	const buildStepData = (step) => {
		switch (step) {
			case 1:
				form.birthday = sanitize(form.birthday)
				form.birthGender = sanitize(form.birthGender).toLowerCase()
				return { birthday: form.birthday, birthGender: form.birthGender }
			case 2:
				form.allergies = form.noAllergies ? "" : sanitize(form.allergies)
				return {
					allergies: form.allergies,
					noAllergies: Boolean(form.noAllergies),
				}
			case 3:
				form.restrictions = form.noRestrictions
					? ""
					: sanitize(form.restrictions)
				return {
					restrictions: form.restrictions,
					noRestrictions: Boolean(form.noRestrictions),
				}
			case 4:
				form.maxCalories = sanitize(form.maxCalories)
				if (form.maxCalories && typeof form.maxCalories !== "string") {
					form.maxCalories = String(form.maxCalories)
				}
				return {
					maxCalories: form.maxCalories,
				}
			case 5:
				form.referralSource = normalizeReferralSource(form.referralSource)
				form.referralSourceOther =
					form.referralSource === "other"
						? normalizeReferralSourceOther(form.referralSourceOther)
						: ""
				return {
					referralSource: form.referralSource,
					referralSourceOther: form.referralSourceOther,
				}
			default:
				return {}
		}
	}

	const applyFormValues = (user, onboardingData, profile) => {
		const source = onboardingData ?? {}
		form.birthday = sanitize(
			source.birthday ?? profile?.birthday ?? user?.birthday ?? ""
		)
		form.birthGender = sanitize(
			source.birthGender ?? profile?.birthGender ?? user?.birthGender ?? ""
		).toLowerCase()
		const allergiesSource =
			source.allergies ?? profile?.allergies ?? user?.allergies
		const restrictionsSource =
			source.restrictions ?? profile?.restrictions ?? user?.restrictions
		const maxCaloriesSource =
			source.maxCalories ?? profile?.maxCalories ?? user?.maxCalories

		const allergiesSanitized = sanitize(allergiesSource ?? "")
		const restrictionsSanitized = sanitize(restrictionsSource ?? "")
		let maxCaloriesString = ""
		if (maxCaloriesSource !== null && maxCaloriesSource !== undefined) {
			const rawValue =
				typeof maxCaloriesSource === "number"
					? String(maxCaloriesSource)
					: String(maxCaloriesSource)
			maxCaloriesString = sanitize(rawValue)
		}

		const noAllergies = Boolean(source.noAllergies ?? false)
		const noRestrictions = Boolean(source.noRestrictions ?? false)

		form.noAllergies = noAllergies
		form.noRestrictions = noRestrictions
		form.allergies = noAllergies ? "" : allergiesSanitized
		form.restrictions = noRestrictions ? "" : restrictionsSanitized
		form.maxCalories = maxCaloriesString
		form.referralSource = normalizeReferralSource(
			source.referralSource ??
				profile?.referralSource ??
				user?.referralSource ??
				""
		)
		form.referralSourceOther = normalizeReferralSourceOther(
			source.referralSourceOther ??
				profile?.referralSourceOther ??
				user?.referralSourceOther ??
				""
		)
	}

	const applyUserState = (user, onboarding = null, profile = null) => {
		if (!user) return
		const onboardingInfo =
			onboarding ?? {
				current_step: user.onboarding_current_step ?? 0,
				completed: Boolean(user.onboarding_completed),
				data: user.onboarding_data ?? null,
			} ??
			null

		const completed = Boolean(onboardingInfo?.completed)
		const stepIndex = completed
			? totalSteps - 1
			: Math.min(
					Math.max(Number(onboardingInfo?.current_step ?? 0), 0),
					totalSteps - 1
			  )

		currentStep.value = stepIndex
		applyFormValues(user, onboardingInfo?.data ?? null, profile)

		if (completed) {
			showFlow.value = false
			markPreferenceComplete()
		} else {
			showFlow.value = true
		}
	}

	const bootstrap = async (user) => {
		const prefComplete = await loadPreferenceComplete()
		if (user.onboarding_completed) {
			showFlow.value = false
			if (!prefComplete) {
				await markPreferenceComplete()
			}
			isLoading.value = false
			return
		}

		try {
			const response = await fetchWithAuth("/api/onboarding")
			if (!response.ok) {
				throw new Error("Unable to fetch onboarding progress.")
			}
			let data = {}
			try {
				data = await response.json()
			} catch {
				data = {}
			}
			applyUserState(user, data.onboarding, data.profile)
		} catch (error) {
			console.warn("[Onboarding] Falling back to user data:", error)
			applyUserState(user)
		} finally {
			isLoading.value = false
		}
	}

	watch(
		() => userStore.user,
		async (user) => {
			if (!user) {
				showFlow.value = false
				return
			}

			if (!hasBootstrapped.value) {
				hasBootstrapped.value = true
				await bootstrap(user)
			} else {
				applyUserState(user)
			}
		},
		{ immediate: true }
	)

	const updateProgress = async ({
		currentStep: step,
		nextStep,
		data = {},
		complete = false,
	}) => {
		savingMessage.value = complete
			? "Finalizing your onboarding…"
			: nextStep > step
			? "Saving your progress…"
			: "Rewinding…"
		isSaving.value = true
		try {
			const response = await fetchWithAuth("/api/onboarding", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					currentStep: step,
					nextStep,
					data,
					complete,
				}),
			})

			let result = {}
			try {
				result = await response.json()
			} catch {
				result = {}
			}
			if (!response.ok) {
				throw new Error(result.message || "Failed to save onboarding progress.")
			}

			if (result.user) {
				userStore.setUser(result.user)
			}

			if (result.onboarding) {
				applyUserState(result.user ?? userStore.user, result.onboarding)
			}

			return result
		} catch (error) {
			await Swal.fire({
				icon: "error",
				title: "Unable to save",
				text: error.message || "Please try again.",
			})
			throw error
		} finally {
			savingMessage.value = ""
			isSaving.value = false
		}
	}

	const goBack = async () => {
		if (isFirstStep.value || isSaving.value) return
		const target = currentStep.value - 1
		try {
			await updateProgress({
				currentStep: currentStep.value,
				nextStep: target,
			})
		} catch {
			// errors already handled
		}
	}

	const goNext = async () => {
		if (isSaving.value) return

		if (isLastStep.value) {
			const validationError = validateStep(currentStep.value)
			if (validationError) {
				await Swal.fire({
					icon: "warning",
					title: "Almost there",
					text: validationError,
				})
				return
			}

			try {
				await updateProgress({
					currentStep: currentStep.value,
					nextStep: currentStep.value,
					data: buildStepData(currentStep.value),
					complete: true,
				})
				dismiss()
			} catch {
				// handled in updateProgress
			}
			return
		}

		const validationError = validateStep(currentStep.value)
		if (validationError) {
			await Swal.fire({
				icon: "warning",
				title: "Almost there",
				text: validationError,
			})
			return
		}

		const target = Math.min(currentStep.value + 1, totalSteps - 1)
		const data = buildStepData(currentStep.value)

		try {
			await updateProgress({
				currentStep: currentStep.value,
				nextStep: target,
				data,
			})
		} catch {
			// error already surfaced by updateProgress
		}
	}

	watch(isLoading, (value) => {
		if (value) {
			savingMessage.value = "Loading your onboarding…"
		} else if (!isSaving.value) {
			savingMessage.value = ""
		}
	})

	watch(isSaving, (value) => {
		if (!value && !isLoading.value) {
			savingMessage.value = ""
		}
	})

	const dismiss = () => {
		showFlow.value = false
		unlockScroll()
	}

	const lockScroll = () => {
		if (typeof document === "undefined") return
		originalOverflow.body = document.body.style.overflow
		originalOverflow.html = document.documentElement.style.overflow
		document.body.style.overflow = "hidden"
		document.documentElement.style.overflow = "hidden"
	}

	function unlockScroll() {
		if (typeof document === "undefined") return
		document.body.style.overflow = originalOverflow.body || ""
		document.documentElement.style.overflow = originalOverflow.html || ""
	}

	watch(
		showFlow,
		(visible) => {
			if (visible) {
				lockScroll()
				scrollActiveStepIntoView()
			} else {
				unlockScroll()
			}
		},
		{ immediate: true }
	)

	onBeforeUnmount(() => {
		unlockScroll()
		stepRefs.length = 0
	})

	function setStepRef(el, index) {
		if (el) {
			stepRefs[index] = el
		}
	}

	async function scrollActiveStepIntoView() {
		await nextTick()
		const activeEl = stepRefs[currentStep.value]
		if (activeEl?.scrollIntoView) {
			activeEl.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
				inline: "center",
			})
		}
	}

	watch(currentStep, () => {
		scrollActiveStepIntoView()
	})

	onMounted(() => {
		if (showFlow.value) {
			scrollActiveStepIntoView()
		}
	})
</script>

<style scoped>
	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.25s ease;
	}

	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
	}

	.onboarding-overlay {
		position: fixed;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		background: rgba(10, 18, 31, 0.6);
		backdrop-filter: blur(6px);
		z-index: 1050;
	}

	.ambient {
		position: absolute;
		border-radius: 50%;
		filter: blur(0);
		opacity: 0.35;
		mix-blend-mode: screen;
		animation: float 16s ease-in-out infinite;
		pointer-events: none;
	}

	.ambient-1 {
		width: 420px;
		height: 420px;
		background: radial-gradient(
			circle,
			rgba(34, 197, 94, 0.4),
			transparent 70%
		);
		top: -6rem;
		left: -6rem;
	}

	.ambient-2 {
		width: 360px;
		height: 360px;
		background: radial-gradient(
			circle,
			rgba(56, 189, 248, 0.4),
			transparent 70%
		);
		bottom: -4rem;
		right: 12%;
		animation-delay: 4s;
	}

	.ambient-3 {
		width: 260px;
		height: 260px;
		background: radial-gradient(
			circle,
			rgba(252, 165, 165, 0.4),
			transparent 70%
		);
		top: 35%;
		right: -8rem;
		animation-delay: 8s;
	}

	.onboarding-shell {
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;
		max-width: 560px;
		min-height: 620px;
		max-height: min(720px, 92vh);
		background: rgba(248, 250, 252, 0.94);
		border-radius: 28px;
		box-shadow: 0 30px 80px rgba(15, 23, 42, 0.22);
		padding: 2.25rem 2rem 2rem;
		backdrop-filter: blur(10px);
		overflow: hidden;
	}

	.onboarding-shell::after {
		content: "";
		position: absolute;
		inset: 0;
		background: linear-gradient(
			135deg,
			rgba(255, 255, 255, 0.65),
			rgba(226, 232, 240, 0.55)
		);
		pointer-events: none;
		z-index: -1;
	}

	.onboarding-header {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		margin-bottom: 1rem;
	}

	.onboarding-badge {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 42px;
		height: 42px;
		background: linear-gradient(135deg, #22c55e, #38bdf8);
		border-radius: 14px;
		color: #fff;
		box-shadow: 0 12px 20px rgba(56, 189, 248, 0.35);
	}

	.onboarding-badge i {
		font-size: 1.25rem;
	}

	.onboarding-sub {
		margin: 0;
		font-size: 0.95rem;
		font-weight: 600;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: #2563eb;
	}

	.onboarding-progress {
		height: 4px;
		background: rgba(15, 23, 42, 0.08);
		border-radius: 999px;
		overflow: hidden;
		margin-bottom: 1.5rem;
	}

	.onboarding-progress-bar {
		height: 100%;
		background: linear-gradient(90deg, #36d399, #22c55e);
		transition: width 0.3s ease;
	}

	.onboarding-steps {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
		gap: 0.6rem;
		margin-bottom: 1.25rem;
	}

	.onboarding-step {
		display: flex;
		align-items: center;
		gap: 0.6rem;
		padding: 0.6rem 0.75rem;
		border-radius: 14px;
		color: #64748b;
		background: rgba(148, 163, 184, 0.16);
		transition: all 0.3s ease;
	}

	.onboarding-step .step-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 34px;
		height: 34px;
		border-radius: 50%;
		background: rgba(148, 163, 184, 0.2);
	}

	.onboarding-step .step-icon i {
		font-size: 1.1rem;
	}

	.onboarding-step.is-complete {
		background: linear-gradient(
			135deg,
			rgba(34, 197, 94, 0.18),
			rgba(59, 130, 246, 0.12)
		);
		color: #0f172a;
		box-shadow: 0 12px 20px rgba(34, 197, 94, 0.18);
	}

	.onboarding-step.is-complete .step-icon {
		background: rgba(34, 197, 94, 0.18);
		color: #15803d;
	}

	.onboarding-step.is-active {
		background: linear-gradient(
			135deg,
			rgba(34, 197, 94, 0.35),
			rgba(59, 130, 246, 0.25)
		);
		color: #0b1120;
		transform: translateY(-2px) scale(1.02);
		box-shadow: 0 15px 35px rgba(59, 130, 246, 0.22);
	}

	.onboarding-step.is-active .step-icon {
		background: rgba(255, 255, 255, 0.65);
		color: #0ea5e9;
	}

	.onboarding-step .step-label {
		font-size: 0.85rem;
		font-weight: 600;
	}

	.slide-fade-enter-active,
	.slide-fade-leave-active {
		transition: opacity 0.35s ease, transform 0.35s ease;
	}

	.slide-fade-enter-from,
	.slide-fade-leave-to {
		opacity: 0;
		transform: translateX(12px);
	}

	.onboarding-body {
		flex: 1;
		overflow: hidden;
	}

	.onboarding-loading {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 100%;
		gap: 1rem;
	}

	.loading-text {
		font-weight: 600;
		color: #1e293b;
	}

	.onboarding-overlay.is-loading .onboarding-shell::before {
		content: "";
		position: absolute;
		inset: 0;
		background: rgba(241, 245, 249, 0.65);
		backdrop-filter: blur(6px);
		z-index: 10;
	}

	.onboarding-overlay.is-loading .onboarding-shell {
		pointer-events: none;
	}

	.onboarding-overlay.is-loading .onboarding-shell > * {
		position: relative;
		z-index: 11;
	}

	.onboarding-overlay.is-loading .onboarding-loading {
		pointer-events: auto;
	}

	:deep(.onboarding-title) {
		font-size: 1.8rem;
		font-weight: 700;
		margin-bottom: 0.65rem;
		color: #0f172a;
	}

	:deep(.onboarding-text) {
		color: #334155;
		line-height: 1.5;
		margin-bottom: 0.75rem;
	}

	:deep(.onboarding-text.lead) {
		font-size: 1.1rem;
		color: #1e293b;
	}

	:deep(.form-group) {
		margin-bottom: 1rem;
	}

	.onboarding-actions {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		margin-top: 1.5rem;
	}

	.onboarding-primary {
		background: linear-gradient(135deg, #16a34a, #2563eb);
		border: none;
		box-shadow: 0 18px 35px rgba(37, 99, 235, 0.28);
		transition: transform 0.25s ease, box-shadow 0.25s ease;
	}

	.onboarding-primary:hover,
	.onboarding-primary:focus {
		transform: translateY(-2px);
		box-shadow: 0 22px 44px rgba(37, 99, 235, 0.35);
	}

	@media (max-width: 768px) {
		.onboarding-overlay {
			padding: 0;
		}

		.onboarding-shell {
			max-width: none;
			width: 100%;
			height: 100%;
			min-height: 100%;
			border-radius: 0;
			padding: 1.5rem 1rem 1.75rem;
		}

		.onboarding-header {
			margin-bottom: 0.75rem;
		}

		.onboarding-sub {
			font-size: 0.78rem;
		}

		.onboarding-progress {
			margin-bottom: 1rem;
		}

		.onboarding-steps {
			display: flex;
			align-items: center;
			gap: 0.5rem;
			overflow-x: auto;
			padding-bottom: 0.35rem;
			margin-bottom: 1rem;
			scroll-snap-type: x mandatory;
		}

		.onboarding-steps::-webkit-scrollbar {
			display: none;
		}

		.onboarding-step {
			min-width: 110px;
			padding: 0.5rem 0.6rem;
			scroll-snap-align: start;
		}

		.onboarding-step .step-icon {
			width: 30px;
			height: 30px;
		}

		.onboarding-body {
			overflow: hidden;
		}

		:deep(.onboarding-title) {
			font-size: 1.35rem;
		}

		:deep(.onboarding-text.lead) {
			font-size: 1rem;
		}

		:deep(.onboarding-text) {
			margin-bottom: 0.6rem;
		}

		:deep(.onboarding-hint) {
			font-size: 0.85rem;
			padding: 0.45rem 0.65rem;
		}

		.onboarding-actions {
			margin-top: 1.25rem;
			gap: 0.75rem;
		}

		.onboarding-actions .btn {
			flex: 1;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.fade-enter-active,
		.fade-leave-active,
		.slide-fade-enter-active,
		.slide-fade-leave-active {
			transition: none;
		}

		.ambient {
			animation: none;
		}
	}

	@keyframes float {
		0%,
		100% {
			transform: translate3d(0, 0, 0);
		}
		50% {
			transform: translate3d(12px, -18px, 0);
		}
	}
</style>
